import { useEffect, useRef, useState } from 'react'
import { useReducedMotion } from 'framer-motion'

/** The backlog is revealed over about this long, so the reveal keeps pace with however fast text arrives. */
const CATCH_UP_SECONDS = 0.3
/** Floor on the reveal rate, so a short backlog still finishes promptly. */
const MIN_CHARS_PER_SECOND = 60

/** Where a reveal that has reached `position` in `text` may stop; at or past `position`. */
export type RevealBoundary = (text: string, position: number) => number

interface SmoothStreamingTextOptions {
  /** Where each step of the reveal stops, e.g. a word end. */
  boundary: RevealBoundary
  /** How long `isRevealing` holds after the reveal catches up, so whatever it last showed can finish fading in. */
  settleMs: number
}

interface SmoothStreamingText {
  text: string
  /** From the start of a reveal until `settleMs` after it catches up with the received text. */
  isRevealing: boolean
}

/**
 * Text arrives in bursts (one per network chunk, coalesced to 100ms); revealing each burst whole
 * lands several pieces at once and then nothing. This advances through the received text at a rate
 * that tracks the backlog, one `boundary` at a time, so they arrive one after another instead.
 *
 * Text already present on mount shows at once, and a target that stops extending what is shown
 * (a regeneration) replaces it outright.
 */
export function useSmoothStreamingText(
  target: string,
  { boundary, settleMs }: SmoothStreamingTextOptions,
): SmoothStreamingText {
  const reduceMotion = useReducedMotion() ?? false
  const [shown, setShown] = useState(target)
  const [isRevealing, setIsRevealing] = useState(false)
  const shownRef = useRef(target)
  const targetRef = useRef(target)
  targetRef.current = target

  useEffect(() => {
    if (reduceMotion) return
    const publish = (text: string) => {
      shownRef.current = text
      setShown(text)
    }
    if (!target.startsWith(shownRef.current)) {
      publish(target)
      setIsRevealing(false)
      return
    }
    if (shownRef.current.length >= target.length) return

    setIsRevealing(true)
    let progress = shownRef.current.length
    let last = performance.now()
    let frame = 0
    let settle: ReturnType<typeof setTimeout> | undefined
    const step = (now: number) => {
      const text = targetRef.current
      const seconds = (now - last) / 1000
      last = now
      progress += Math.max(MIN_CHARS_PER_SECOND, (text.length - progress) / CATCH_UP_SECONDS) * seconds
      const end = boundary(text, Math.floor(progress))
      if (end > shownRef.current.length) publish(text.slice(0, end))
      // Stop once the reveal has worked through everything received, not once it has published it:
      // a boundary can hold the tail back for good, and waiting on it would spin a frame loop until
      // the next chunk. That chunk changes `target` and starts a fresh loop.
      if (progress < text.length) frame = requestAnimationFrame(step)
      else settle = setTimeout(() => setIsRevealing(false), settleMs)
    }
    frame = requestAnimationFrame(step)
    return () => {
      cancelAnimationFrame(frame)
      clearTimeout(settle)
    }
  }, [target, reduceMotion, boundary, settleMs])

  return reduceMotion ? { text: target, isRevealing: false } : { text: shown, isRevealing }
}
